import { useEffect, useRef, useState, RefObject } from 'react';
import { interpolateViridis } from 'd3';

/**
 * Default Alphasense OPC-N3 bin boundaries in µm: 25 edges bounding 24 bins
 * (bin i spans [edge i, edge i+1)). Users can override these by uploading an
 * Excel sheet on the OPC bins page — real deployments sometimes recalibrate.
 */
export const DEFAULT_OPC_N3_BOUNDARIES: number[] = [
  0.35, 0.46, 0.66, 1.0, 1.3, 1.7, 2.3, 3.0, 4.0, 5.2, 6.5, 8.0, 10.0, 12.0, 14.0, 16.0, 18.0,
  20.0, 22.0, 25.0, 28.0, 31.0, 34.0, 37.0, 40.0
];

export const OPC_BIN_COUNT = 24;

/** Concentration unit the OPC-N3 pipeline reports bin counts in. */
export const CONC_UNIT = '#/ml';

/** LocalStorage key for user-uploaded bin boundaries. */
export const BOUNDARIES_STORAGE_KEY = 'opc_bin_boundaries_v1';

/** The TSI APS-3321 reports 52 channels. */
export const APS_CHANNEL_COUNT = 52;

/**
 * What the APS endpoints return: counts divided by the accumulation period.
 * Rates rather than raw counts because the sample period varies between records.
 */
export const APS_RATE_UNIT = 'counts/s';

/** Derived number-concentration unit, once a sample flow is assumed. */
export const APS_CONC_UNIT = '#/cm³';

/**
 * APS-3321 aerosol sample flow, 1.0 L/min, expressed as cm³/s. Dividing a count
 * rate by this converts it to a number concentration.
 *
 * NOTE: this is the instrument's nominal specification, not a measured value read
 * back from the record — the D-record carries no flow field. The UI labels any axis
 * derived from it as an assumption. Confirm against the instrument's actual
 * configuration before treating those numbers as calibrated.
 */
export const APS_AEROSOL_FLOW_CM3_PER_S = 1000 / 60;

/** LocalStorage key for user-uploaded APS channel boundaries. */
export const APS_BOUNDARIES_STORAGE_KEY = 'aps_channel_boundaries_v1';

/**
 * Default APS-3321 channel edges in µm: 53 edges bounding 52 channels.
 *
 * Channel 1 is the under-range bin (everything below 0.523 µm); it has no real
 * lower edge, so a nominal 0.3 stands in to keep the axis positive and ascending.
 * Channels 2–52 are log-spaced from 0.523 to 19.81 µm, the instrument's stated
 * range, which works out at roughly 32 channels per decade.
 *
 * Computed rather than transcribed so the spacing is visibly uniform; the exact
 * factory boundaries can be uploaded as a spreadsheet if they differ.
 */
export const DEFAULT_APS_BOUNDARIES: number[] = (() => {
  const first = 0.523;
  const last = 19.81;
  const steps = APS_CHANNEL_COUNT - 1;
  const ratio = (last / first) ** (1 / steps);
  const edges = [0.3];
  for (let i = 0; i <= steps; i += 1) {
    edges.push(first * ratio ** i);
  }
  return edges;
})();

/**
 * Format one boundary value: trim float noise, keep sub-µm precision.
 * @param value - Boundary in µm.
 * @returns Compact label like "0.35" or "10".
 */
export function fmtBoundary(value: number): string {
  return String(Number(value.toFixed(2)));
}

/**
 * Label for a bin's particle-size range, e.g. "0.35–0.46".
 * @param edges - The 25 bin edges (µm).
 * @param bin - Bin index 0..23.
 * @returns Size-range label (no unit suffix).
 */
export function binRangeLabel(edges: number[], bin: number): string {
  if (edges.length >= bin + 2) {
    return `${fmtBoundary(edges[bin])}–${fmtBoundary(edges[bin + 1])}`;
  }
  return `bin ${bin}`;
}

/**
 * Fraction of the viridis ramp skipped at the bottom. Viridis starts at a very
 * dark purple (#440154) that is nearly invisible against the charts' #111827
 * panel, which made the low end of every plot unreadable. Starting at 0.35
 * enters the ramp at a mid teal, so the dimmest value still reads clearly while
 * lightness keeps increasing monotonically towards the bright yellow top.
 */
const RAMP_FLOOR = 0.35;

/**
 * Sequential color for a normalized value, on a ramp tuned for a dark panel.
 * @param t - Normalized 0..1.
 * @returns CSS color.
 */
export function binColor(t: number): string {
  const clamped = Math.max(0, Math.min(1, t));
  return interpolateViridis(RAMP_FLOOR + clamped * (1 - RAMP_FLOOR));
}

/**
 * Colors for time slices picked on the contour plot.
 *
 * Deliberately outside the viridis ramp used for concentration — pinks, oranges
 * and reds never appear in the fill, so a selection line is always separable from
 * the surface underneath it. White leads so a single selection looks as it did
 * before multi-select existed. The list cycles if more are picked.
 */
export const SLICE_COLORS = [
  '#f9fafb',
  '#f472b6',
  '#fb923c',
  '#60a5fa',
  '#f87171',
  '#a78bfa'
];

/**
 * Color for the nth picked time slice.
 * @param index - Selection order.
 * @returns CSS color.
 */
export function sliceColor(index: number): string {
  return SLICE_COLORS[index % SLICE_COLORS.length];
}

/**
 * Normalize a concentration for coloring. Bin 0 dominates by orders of
 * magnitude, so the default is a log ramp; linear is available for comparing
 * absolute levels.
 * @param value - Raw concentration.
 * @param max - Maximum concentration in the dataset.
 * @param log - Use log1p ramp when true.
 * @returns Normalized 0..1.
 */
export function normalizeConc(value: number, max: number, log: boolean): number {
  if (max <= 0) return 0;
  if (!log) return value / max;
  return Math.log1p(Math.max(0, value)) / Math.log1p(max);
}

/**
 * Compact concentration formatter for axis/tooltip text.
 * @param value - Concentration.
 * @returns Formatted string.
 */
export function fmtConc(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  if (value === 0) return '0';
  const abs = Math.abs(value);
  if (abs >= 100) return value.toFixed(0);
  if (abs >= 1) return String(Number(value.toFixed(2)));
  if (abs >= 0.01) return String(Number(value.toFixed(3)));
  return value.toExponential(1);
}

/**
 * Time tick formatter that adapts to the plotted span.
 * @param ts - Timestamp millis.
 * @param spanMs - Total plotted span millis.
 * @returns Short label.
 */
export function fmtTimeTick(ts: number, spanMs: number): string {
  const d = new Date(ts);
  if (spanMs > 3 * 86400_000) {
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  if (spanMs > 86400_000) {
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/**
 * Track an element's rendered width so SVG charts can be responsive.
 * @param ref - Ref of the element to observe.
 * @returns Current width in px (0 before first measure).
 */
export function useElementWidth(ref: RefObject<HTMLElement>): number {
  const [width, setWidth] = useState(0);
  const frame = useRef(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const observer = new ResizeObserver((entries) => {
      cancelAnimationFrame(frame.current);
      const w = entries[0]?.contentRect.width ?? 0;
      frame.current = requestAnimationFrame(() => setWidth(w));
    });
    observer.observe(el);
    return () => {
      cancelAnimationFrame(frame.current);
      observer.disconnect();
    };
  }, [ref]);
  return width;
}

/** Shared card chrome for the OPC charts. */
export const CARD_CLASS = 'rounded-lg border border-gray-800 bg-container-500 p-4';

/**
 * Shared small toggle-button chrome.
 * @param active - Whether the toggle is on.
 * @returns Tailwind class string.
 */
export function toggleClass(active: boolean): string {
  return `rounded border px-3 py-1.5 text-base transition ${
    active
      ? 'border-primary-500/60 bg-primary-500/20 text-primary-400'
      : 'border-gray-800 bg-background-700 text-gray-400 hover:text-gray-200'
  }`;
}
